import Breadcrumbs from 'Components/Breadcrumbs'
import CustomButton from 'Components/CustomButton'
import ListActions from 'Components/ListActions'
import ListSearch from 'Components/ListSearch'
import { WithLoader } from 'Components/WithLoader'
import ServiceProviderDrawer from 'Services/ServiceProviderManagement/Features/ServiceProvider/Components/ServiceProviderDrawer'
import ServiceProviderList from 'Services/ServiceProviderManagement/Features/ServiceProvider/Components/ServiceProviderList'
import useServiceProvidersWithParams from 'Services/ServiceProviderManagement/Features/ServiceProvider/Hooks/useServiceProvidersWithParams'
import { useDeleteSingleServiceProviderMutation } from 'Services/ServiceProviderManagement/Features/ServiceProvider/serviceProviderApi'
import { ServiceProviderType } from 'Services/ServiceProviderManagement/Features/ServiceProvider/types'
import {
  IS_HSL_SERVICE_ENVIRONMENT,
  ROWS_PER_PAGE_DEFAULT,
  SERVICE_PROVIDER_MANAGEMENT_ROOT_URL,
  TAB_PARAM_KEY
} from 'Util/constants'
import useListFunctions from 'Util/hooks/useListFunctions'
import SnackbarUtils from 'Util/Snackbar'
import AddIcon from '@mui/icons-material/Add'
import { TabList } from '@mui/lab'
import TabContext from '@mui/lab/TabContext'
import { Box, Tab, Typography } from '@mui/material'
import { useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { t } from 'ttag'

export const SERVICE_PROVIDER_DETAILS_DRAWER_TAB_PARAM_KEY = 'serviceProviderDetailsDrawerTab'

export default function ServiceProviderListPage() {
  const baseUrl = `${SERVICE_PROVIDER_MANAGEMENT_ROOT_URL}/service-providers`
  const navigate = useNavigate()
  const {
    serviceProviders,
    isFetching: isServiceProvidersFetching,
    totalResults,
    searchQueryParams,
    setSearchQueryParams,
    selectedServiceProviderId,
    setSelectedServiceProviderId
  } = useServiceProvidersWithParams()
  const [searchParams, setSearchParams] = useSearchParams()
  const tabFromParams = searchParams.get(TAB_PARAM_KEY) ?? ''

  const [deleteServiceProvider, { isLoading: isDeleteLoading }] =
    useDeleteSingleServiceProviderMutation()

  const [isDrawerOpen, setIsDrawerOpen] = useState(!!selectedServiceProviderId)
  const breadcrumbs = [
    {
      title: t`Home`,
      link: SERVICE_PROVIDER_MANAGEMENT_ROOT_URL
    },
    {
      title: t`Service providers`
    }
  ]

  const { searchText, handleChangePage, handleRequestSort, handleSearchTextChange, handleSearch } =
    useListFunctions({
      searchQueryParams,
      setSearchQueryParams
    })

  const handleDrawerClose = () => {
    setSelectedServiceProviderId('')
    setIsDrawerOpen(false)
    searchParams.delete(SERVICE_PROVIDER_DETAILS_DRAWER_TAB_PARAM_KEY)
    setSearchParams(searchParams)
  }

  const handleRowClick = (id: string) => {
    setSelectedServiceProviderId(id)
    setIsDrawerOpen(true)
  }

  const walttiServiceProviderTypes = [
    {
      label: t`Traffic contractor`,
      value: ServiceProviderType.TRAFFIC_CONTRACTOR
    },
    {
      label: t`U Traffic contractor`,
      value: ServiceProviderType.U_TRAFFIC_CONTRACTOR
    },
    {
      label: t`Sales point`,
      value: ServiceProviderType.SALES_POINT
    },
    {
      label: t`Service point`,
      value: ServiceProviderType.SERVICE_POINT
    },
    {
      label: t`MaaS`,
      value: ServiceProviderType.MAAS
    },
    {
      label: t`Other`,
      value: ServiceProviderType.OTHER
    },
    {
      label: t`Ticket machine`,
      value: ServiceProviderType.TICKET_MACHINE
    },
    {
      label: t`Webshop`,
      value: ServiceProviderType.WEBSHOP
    },
    {
      label: t`Mobile`,
      value: ServiceProviderType.MOBILE
    },
    {
      label: t`Event ticket`,
      value: ServiceProviderType.EVENT_TICKET
    }
  ]

  const hslServiceProviderTypes = [
    {
      label: t`Traffic contractor`,
      value: ServiceProviderType.TRAFFIC_CONTRACTOR
    },
    {
      label: t`U Traffic contractor`,
      value: ServiceProviderType.U_TRAFFIC_CONTRACTOR
    },
    {
      label: t`Sales point`,
      value: ServiceProviderType.SALES_POINT
    },
    {
      label: t`Service point`,
      value: ServiceProviderType.SERVICE_POINT
    },
    {
      label: t`HSL digital channel`,
      value: ServiceProviderType.HSL_DIGITAL_CHANNEL
    },
    {
      label: t`MaaS`,
      value: ServiceProviderType.MAAS
    },
    {
      label: t`Other`,
      value: ServiceProviderType.OTHER
    }
  ]

  const serviceProviderTypes = IS_HSL_SERVICE_ENVIRONMENT
    ? hslServiceProviderTypes
    : walttiServiceProviderTypes

  const tabOptions = [{ label: t`All`, value: '' }, ...serviceProviderTypes]

  const handleTabChange = (_event: React.SyntheticEvent | null, newValue: string) => {
    setSearchQueryParams(prevState => {
      const newParams = { ...prevState, start_index: undefined }

      if (newValue) {
        newParams.type = newValue as ServiceProviderType
      } else {
        // Remove type if newValue is ''
        delete newParams.type
        searchParams.delete('type')
      }

      return newParams
    })
    searchParams.set(TAB_PARAM_KEY, newValue)
    setSearchParams(searchParams)
  }

  const handleDelete = () => {
    if (!selectedServiceProviderId) return
    deleteServiceProvider(selectedServiceProviderId)
      .unwrap()
      .then(() => {
        handleDrawerClose()
        SnackbarUtils.success(t`Service provider successfully deleted.`)
      })
      .catch(() => {
        SnackbarUtils.error(t`There was an error deleting the service provider.`)
      })
  }

  const isLoading = isServiceProvidersFetching || isDeleteLoading

  return (
    <Box sx={{ width: '100%' }}>
      <Breadcrumbs items={breadcrumbs} />
      <Box sx={{ mb: 2 }}>
        <Typography variant="h1" sx={{ mb: 2 }}>{t`Service providers`}</Typography>
        <Box sx={{ mt: 4 }}>
          <ListActions>
            <CustomButton
              onClick={() => navigate(`${baseUrl}/create/`)}
              size="small"
              startIcon={<AddIcon />}
            >
              {t`Create new service provider`}
            </CustomButton>
            <ListSearch
              searchText={searchText}
              handleSearchTextChange={handleSearchTextChange}
              handleSearch={handleSearch}
              placeholder={t`Search by name or code`}
            />
          </ListActions>
          <TabContext value={tabFromParams}>
            <TabList
              onChange={handleTabChange}
              aria-label={t`Product group tab list`}
              variant="scrollable"
            >
              {tabOptions?.map(({ label, value }) => (
                <Tab key={value} label={label} value={value} />
              ))}
            </TabList>
            <Box sx={{ pt: 1 }}>
              <WithLoader loading={isLoading} rows={ROWS_PER_PAGE_DEFAULT} height={33}>
                <ServiceProviderList
                  rows={serviceProviders}
                  isLoading={isLoading}
                  params={searchQueryParams}
                  handleRequestSort={handleRequestSort}
                  handleChangePage={handleChangePage}
                  count={totalResults}
                  page={
                    searchQueryParams.start_index
                      ? Number.parseInt(searchQueryParams.start_index, 10) / ROWS_PER_PAGE_DEFAULT
                      : 0
                  }
                  onRowClick={handleRowClick}
                  selectedServiceProviderId={selectedServiceProviderId}
                />
              </WithLoader>
            </Box>
          </TabContext>
          <ServiceProviderDrawer
            open={isDrawerOpen}
            handleClose={handleDrawerClose}
            handleServiceProviderDelete={handleDelete}
          />
        </Box>
      </Box>
    </Box>
  )
}
